import React, { useEffect, useMemo, useState } from 'react';
import { getAdminLearningContentTemplates, getAdminLearningContentRows } from '../../services/adminLearningContentService';
import {
  getWorkspaceLearningContentTemplates,
  getWorkspaceLearningContentRows,
} from '../../services/workspaceLearningContentService';
import type { AdminLearningContentRow, AdminLearningContentSubCategory } from '../../types/domain';
import {
  EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION,
  LearningContentTemplateSelection,
  findPreferredLearningContentTemplateName,
  programTermNumberToLearningSubCategory,
  resolveLearningContentTemplateSelection,
} from '../../utils/learningContentTemplateSelection';

const GRADE_TABS = [1, 2, 3, 4, 5, 6] as const;
const SUB_CATEGORIES: Array<{ key: AdminLearningContentSubCategory; label: (grade: number) => string }> = [
  { key: 'term1', label: (grade) => `${grade}학년 1학기` },
  { key: 'term2', label: (grade) => `${grade}학년 2학기` },
  { key: 'common', label: (grade) => `${grade}학년 학기 구분 없음` },
];

export type ProgramImportLearningContentModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  gradeId: string;
  defaultGradeNumber: (typeof GRADE_TABS)[number];
  /** 프로그램 학기. 창을 열면 해당 학기 탭이 선택된다. */
  termNumber?: 1 | 2 | null;
  /** 현재 프로그램 편제명. 학습내용 편제명이 달라도 가까운 항목을 고른다. */
  preferredTemplateName?: string;
  applying: boolean;
  onApply: (rows: AdminLearningContentRow[]) => Promise<void>;
};

const ReadonlyAdminTemplateTabItem: React.FC<{
  name: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
}> = ({ name, active, busy, onSelect }) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item cp-admin-learning-template-item--readonly${active ? ' is-active' : ''}`}
    aria-selected={active}
    aria-label={`${name}, 관리자 기본 편제`}
    title="관리자 메뉴「학습내용 관리」에서 등록된 기본 편제"
    onClick={() => {
      if (!busy) onSelect();
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!busy) onSelect();
      }
    }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{name}</span>
    </div>
  </div>
);

const PersonalTemplateTabItem: React.FC<{
  name: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
}> = ({ name, active, busy, onSelect }) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item${active ? ' is-active' : ''}`}
    aria-selected={active}
    aria-label={`${name}, 사용자 편제`}
    title="이 워크스페이스에서 추가한 사용자 편제"
    onClick={() => {
      if (!busy) onSelect();
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!busy) onSelect();
      }
    }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{name}</span>
    </div>
  </div>
);

const ProgramImportLearningContentModal: React.FC<ProgramImportLearningContentModalProps> = ({
  open,
  onClose,
  workspaceId,
  gradeId,
  defaultGradeNumber,
  termNumber,
  preferredTemplateName = '',
  applying,
  onApply,
}) => {
  const scopeGradeId = gradeId.trim();
  const defaultSubCategory = programTermNumberToLearningSubCategory(termNumber);
  const [gradeNumber, setGradeNumber] = useState<(typeof GRADE_TABS)[number]>(defaultGradeNumber);
  const [userSubCategory, setUserSubCategory] = useState<AdminLearningContentSubCategory | null>(null);
  const subCategory = userSubCategory ?? defaultSubCategory;
  const [adminTemplateNames, setAdminTemplateNames] = useState<string[]>([]);
  const [personalTemplates, setPersonalTemplates] = useState<string[]>([]);
  const [templateSelection, setTemplateSelection] = useState<LearningContentTemplateSelection>(
    EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION
  );
  const templateName = templateSelection.name;
  const templateScope = templateSelection.scope;
  const [publisherFilter, setPublisherFilter] = useState('');
  const [fetchedRows, setFetchedRows] = useState<AdminLearningContentRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const isAdminTemplate = templateScope === 'admin';

  useEffect(() => {
    if (!open) {
      setUserSubCategory(null);
      return;
    }
    setGradeNumber(defaultGradeNumber);
    setTemplateSelection(EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION);
    setPublisherFilter('');
  }, [defaultGradeNumber, open]);

  useEffect(() => {
    if (!open || !workspaceId || !scopeGradeId) {
      setAdminTemplateNames([]);
      setPersonalTemplates([]);
      setTemplateSelection(EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION);
      setTemplatesLoading(false);
      setRowsLoading(false);
      return;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    void (async () => {
      try {
        const [adminList, wsList] = await Promise.all([
          // LS + head(updated_at) 비교 후 변경 시에만 풀 fetch
          getAdminLearningContentTemplates(workspaceId, gradeNumber, subCategory).catch(() => []),
          getWorkspaceLearningContentTemplates(workspaceId, scopeGradeId, gradeNumber, subCategory).catch(
            () => []
          ),
        ]);
        if (cancelled) return;
        const adminNames = (adminList ?? [])
          .map((row) => (row.template_name ?? '').trim())
          .filter(Boolean);
        const personalNames = (wsList ?? [])
          .map((row) => (row.template_name ?? '').trim())
          .filter(Boolean);

        const preferred = preferredTemplateName.trim();
        const preferredInCurrent = preferred
          ? findPreferredLearningContentTemplateName(preferred, adminNames) ||
            findPreferredLearningContentTemplateName(preferred, personalNames)
          : null;

        // 1·2학기 탭에 동일 편제가 없고 「학기 구분 없음」에만 있으면(음악 등) 그 탭으로 이동
        if (
          preferred &&
          !preferredInCurrent &&
          userSubCategory == null &&
          (subCategory === 'term1' || subCategory === 'term2')
        ) {
          const [commonAdminList, commonWsList] = await Promise.all([
            getAdminLearningContentTemplates(workspaceId, gradeNumber, 'common').catch(() => []),
            getWorkspaceLearningContentTemplates(
              workspaceId,
              scopeGradeId,
              gradeNumber,
              'common'
            ).catch(() => []),
          ]);
          if (cancelled) return;
          const commonAdminNames = (commonAdminList ?? [])
            .map((row) => (row.template_name ?? '').trim())
            .filter(Boolean);
          const commonPersonalNames = (commonWsList ?? [])
            .map((row) => (row.template_name ?? '').trim())
            .filter(Boolean);
          const preferredInCommon =
            findPreferredLearningContentTemplateName(preferred, commonAdminNames) ||
            findPreferredLearningContentTemplateName(preferred, commonPersonalNames);
          if (preferredInCommon) {
            setUserSubCategory('common');
            return;
          }
        }

        if (
          adminNames.length === 0 &&
          personalNames.length === 0 &&
          userSubCategory == null &&
          (subCategory === 'term1' || subCategory === 'term2')
        ) {
          setUserSubCategory('common');
          return;
        }
        setAdminTemplateNames(adminNames);
        setPersonalTemplates(personalNames);
        setTemplateSelection(
          resolveLearningContentTemplateSelection(
            EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION,
            adminNames,
            personalNames,
            [],
            preferredTemplateName
          )
        );
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gradeNumber, open, preferredTemplateName, scopeGradeId, subCategory, userSubCategory, workspaceId]);

  useEffect(() => {
    if (!open || !workspaceId || !scopeGradeId || !templateName.trim()) {
      setFetchedRows([]);
      setRowsError(null);
      setRowsLoading(false);
      return;
    }
    let cancelled = false;
    setRowsLoading(true);
    setRowsError(null);
    setFetchedRows([]);
    // LS + head(updated_at) 비교 후 변경 시에만 풀 fetch (출판사 목록도 rows에서 파생)
    const load = isAdminTemplate
      ? getAdminLearningContentRows(workspaceId, gradeNumber, subCategory, templateName)
      : getWorkspaceLearningContentRows(
          workspaceId,
          scopeGradeId,
          gradeNumber,
          subCategory,
          templateName
        );
    void load
      .then((list) => {
        if (cancelled) return;
        setFetchedRows(list ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        setFetchedRows([]);
        setRowsError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setRowsLoading(false);
      });
    return () => {
      cancelled = true;
      setRowsLoading(false);
    };
  }, [gradeNumber, isAdminTemplate, open, scopeGradeId, subCategory, templateName, templateScope, workspaceId]);

  const publisherOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of fetchedRows) {
      const t = (r.publisher ?? '').trim();
      if (!t) continue;
      map.set(t, t);
    }
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ko'));
  }, [fetchedRows]);

  const showPublisherSubtabs = !!templateName.trim() && publisherOptions.length > 0;

  const hasTemplateTabs = adminTemplateNames.length > 0 || personalTemplates.length > 0;
  const showTemplateEmptyPanel = !templatesLoading && !hasTemplateTabs;

  useEffect(() => {
    if (!templateName.trim()) return;
    setPublisherFilter((prev) => {
      if (publisherOptions.length === 0) return '';
      if (publisherOptions.some(([key]) => key === prev)) return prev;
      return publisherOptions[0][0];
    });
  }, [publisherOptions, templateName]);

  const rowsForImport = useMemo(() => {
    const withPublisher = fetchedRows.filter((r) => (r.publisher ?? '').trim().length > 0);
    if (withPublisher.length === 0) return fetchedRows;
    if (!showPublisherSubtabs) return withPublisher;
    return withPublisher.filter((r) => (r.publisher ?? '').trim() === publisherFilter);
  }, [fetchedRows, publisherFilter, showPublisherSubtabs]);

  /** 편제 목록 로딩·데이터 가져오기 적용 중에만 상단 컨트롤을 잠금(연해짐). 행(rows)만 불러올 때는 텍스트만 표시해 깜박임을 줄임 */
  const chromeBusy = applying || templatesLoading;
  const rowsFetchBusy = !!templateName.trim() && rowsLoading;
  const canApply =
    !!templateName.trim() &&
    (!showPublisherSubtabs || !!publisherFilter) &&
    rowsForImport.length > 0 &&
    !chromeBusy &&
    !rowsFetchBusy &&
    !rowsError;

  if (!open) return null;

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !chromeBusy) onClose();
      }}
    >
      <div className="cp-modal cp-program-import-learning-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
        <h3 className="cp-program-import-learning-modal-title">기본 학습내용 가져오기</h3>

        <div className="cp-program-import-learning-modal-scroll-host">
          <div className="cp-program-import-learning-modal-centered">
            <div className="cp-program-import-learning-modal-tabs-stack">
              <div className="cp-program-import-learning-grade-tabs" role="tablist" aria-label="학년">
              {GRADE_TABS.map((grade) => (
                <button
                  key={grade}
                  type="button"
                  role="tab"
                  aria-selected={gradeNumber === grade}
                  aria-disabled={chromeBusy}
                  className={`cp-program-import-learning-grade-tab${gradeNumber === grade ? ' is-active' : ''}${
                    chromeBusy ? ' is-loading-lock' : ''
                  }`}
                  onClick={() => {
                    if (chromeBusy) return;
                    setGradeNumber(grade);
                  }}
                >
                  {grade}
                </button>
              ))}
              </div>
              <div className="cp-admin-learning-sub-tabs cp-program-import-learning-modal-sub-tabs" role="tablist" aria-label="학기">
              {SUB_CATEGORIES.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  role="tab"
                  aria-selected={subCategory === category.key}
                  className={`cp-admin-learning-sub-tab cp-admin-learning-sub-tab--${category.key}${
                    subCategory === category.key ? ' is-active' : ''
                  }`}
                  disabled={chromeBusy}
                  onClick={() => setUserSubCategory(category.key)}
                >
                  {category.label(gradeNumber)}
                </button>
              ))}
              </div>
            </div>

            <div className="cp-program-import-learning-template-block cp-program-import-learning-template-block--modal">
              <div className="cp-program-import-learning-template-slot">
              <div className="cp-program-import-learning-template-region">
                <p className="cp-program-import-learning-modal-section-title">편제 선택</p>
                {templatesLoading ? (
                  <div className="cp-program-import-learning-import-field-spacer" aria-busy="true" />
                ) : showTemplateEmptyPanel ? (
                  <div className="cp-program-import-learning-empty-panel" role="status">
                    <p className="cp-program-import-learning-empty-panel-message">이 학년·학기에 등록된 편제가 없습니다.</p>
                  </div>
                ) : (
                  <div className="cp-admin-learning-template-tabs cp-program-import-learning-modal-template-tabs" role="tablist" aria-label="편제">
                    {adminTemplateNames.map((name) => (
                      <ReadonlyAdminTemplateTabItem
                        key={`admin:${name}`}
                        name={name}
                        active={templateScope === 'admin' && templateName === name}
                        busy={chromeBusy}
                        onSelect={() => setTemplateSelection({ scope: 'admin', name })}
                      />
                    ))}
                    {personalTemplates.map((name) => (
                      <PersonalTemplateTabItem
                        key={`ws:${name}`}
                        name={name}
                        active={templateScope === 'user' && templateName === name}
                        busy={chromeBusy}
                        onSelect={() => setTemplateSelection({ scope: 'user', name })}
                      />
                    ))}
                  </div>
                )}
              </div>
              <div className="cp-program-import-learning-publisher-slot">
                <p className="cp-program-import-learning-modal-section-title">출판사 학습내용 선택</p>
                {showPublisherSubtabs && !rowsFetchBusy ? (
                  <div className="cp-program-import-learning-publisher-subtabs" role="tablist" aria-label="출판사">
                    {publisherOptions.map(([key, label]) => {
                      const active = publisherFilter === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={`cp-program-import-learning-publisher-subtab${active ? ' is-active' : ''}`}
                          disabled={chromeBusy}
                          onClick={() => setPublisherFilter(key)}
                        >
                          <span className="cp-program-import-learning-publisher-led" aria-hidden>
                            {active ? <span className="cp-program-import-learning-publisher-led-mark">v</span> : null}
                          </span>
                          <span className="cp-program-import-learning-publisher-subtab-label">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : rowsFetchBusy ? (
                  <div className="cp-program-import-learning-import-field-spacer" aria-busy="true" />
                ) : (
                  <div className="cp-program-import-learning-empty-panel" role="status">
                    <p className="cp-program-import-learning-empty-panel-message">
                      {!templateName.trim()
                        ? '편제를 선택하면 출판사별 버튼이 표시됩니다.'
                        : rowsError
                          ? '학습내용을 불러오지 못했습니다.'
                          : '이 편제에 등록된 출판사별 학습내용이 없습니다.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="cp-program-import-learning-modal-body">
          <div className="cp-program-import-learning-status">
            {rowsError ? (
              <p className="page-desc cp-program-import-learning-error" role="alert">
                {rowsError}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="cp-modal-footer cp-program-import-learning-modal-footer">
          <button type="button" className="cp-btn-secondary" disabled={chromeBusy} onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={!canApply}
            onClick={() => {
              if (!canApply) return;
              void onApply(rowsForImport);
            }}
          >
            데이터 가져오기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramImportLearningContentModal;
